import { FC } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { useFeatureFlags } from 'src/hooks/use-feature-flags';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { getServices } from 'src/pages/settings/notifications/config';
import { EditNotificationSettings } from 'src/pages/settings/notifications/edit';
import { NotificationSettingsOverview } from 'src/pages/settings/notifications/overview';
import { EditShipperNotificationSettings } from 'src/pages/settings/notifications/shipper-edit';
import {
  CurrentShipperUserRequest,
  useCurrentShipperUser,
} from 'src/pages/settings/notifications/use-current-shipper-user';
import {
  NotificationSettingsRequest,
  useNotificationSettings,
} from 'src/pages/settings/notifications/use-notification-settings';
import {
  ShipperRequest,
  useShipper,
} from 'src/pages/shipper-admin/details/use-shipper';

export const NotificationSettings: FC = () => {
  const featureFlags = useFeatureFlags();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const services = getServices(monolithUser);
  const notificationSettings = useNotificationSettings({ services });

  if (monolithUser?.isShipper && featureFlags.userInvitations)
    return <LoadShipperData notificationSettings={notificationSettings} />;

  return <Routing notificationSettings={notificationSettings} />;
};

const LoadShipperData: FC<{
  notificationSettings: NotificationSettingsRequest;
}> = ({ notificationSettings }) => {
  const shipperUser = useCurrentShipperUser();
  const shipper = useShipper();
  return (
    <Routing
      notificationSettings={notificationSettings}
      shipperUser={shipperUser}
      shipper={shipper}
    />
  );
};

const Routing: FC<{
  notificationSettings: NotificationSettingsRequest;
  shipperUser?: CurrentShipperUserRequest;
  shipper?: ShipperRequest;
}> = ({ notificationSettings, shipperUser, shipper }) => {
  if (
    notificationSettings?.response === null ||
    shipperUser?.response === null ||
    shipper?.response === null
  )
    return <PageSpinner />;

  return (
    <>
      <Heading mode="title">Notification Settings</Heading>
      <Switch>
        {notificationSettings ? (
          <Route path="/settings/notifications/edit" exact>
            <EditNotificationSettings
              notificationSettings={notificationSettings}
            />
          </Route>
        ) : null}

        {shipperUser && shipper ? (
          <Route path="/settings/notifications/shipper-edit" exact>
            <EditShipperNotificationSettings
              shipperUser={shipperUser}
              shipper={shipper}
            />
          </Route>
        ) : null}

        <Route path="/settings/notifications" exact>
          <NotificationSettingsOverview
            notificationSettings={notificationSettings}
            shipperUser={shipperUser}
            shipper={shipper}
          />
        </Route>

        <Route>
          <ThrowNotFound />
        </Route>
      </Switch>
    </>
  );
};
